import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Row,
  Col,
  Badge,
  Form,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { Calendar, Search } from "lucide-react";

const SalesChart = () => {
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TUTTI");
  const [tempDates, setTempDates] = useState({ start: "", end: "" });
  const [appliedDates, setAppliedDates] = useState({ start: "", end: "" });
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetch("./ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordiniRaw = json?.STATO_ORDINI?.flat() || [];
        const sorted = ordiniRaw.sort((a, b) =>
          String(a["Num Doc"]).localeCompare(String(b["Num Doc"]))
        );
        setUrgentOrders(sorted);
      })
      .catch((err) => console.error("Errore:", err));
  }, []);

  const getStatusButtonClass = (stato) => {
    const map = {
      "ORDINE CONFERMATO": "btn-secondary",
      "IN ARRIVO": "btn-danger",
      "IN MAGAZZINO": "btn-teal",
      "PRESO IN CARICO": "btn-orange",
      "BEM INVIATA": "btn-purple",
      "BEM COMPLETATA": "btn-dark",
      "INVIO ODP/ODPV": "btn-warning",
      "COMPLETAMENTO ODP/ODPV": "btn-info",
      "GESTITO": "btn-indigo",
      "CHIUSO CON BOLLA": "btn-primary",
      "COMPLETATO": "btn-success",
    };
    return map[stato] || "btn-secondary";
  };

  // --- LOGICA DI RICERCA POTENZIATA ---
  // Filtriamo gli ordini basandoci su Fornitore, Cliente, Numero Doc o Codice Articolo
  const filteredOrders = urgentOrders.filter((order) => {
    const orderDate = new Date(order["Data Ordine"]).toISOString().split("T")[0];
    const s = searchTerm.toLowerCase();
    
    const matchesSearch = 
      order["Num Doc"]?.toString().toLowerCase().includes(s) ||
      order["Ragione sociale"]?.toString().toLowerCase().includes(s) ||
      order["Rag Soc Cli"]?.toString().toLowerCase().includes(s);

    const matchesStatus = statusFilter === "TUTTI" || order.STATO === statusFilter;
    const matchesStart = appliedDates.start === "" || orderDate >= appliedDates.start;
    const matchesEnd = appliedDates.end === "" || orderDate <= appliedDates.end;

    return matchesSearch && matchesStatus && matchesStart && matchesEnd;
  });

  const handleApplyDates = () => {
    setAppliedDates(tempDates);
    document.body.click();
  };

  const handleResetDates = () => {
    const empty = { start: "", end: "" };
    setTempDates(empty);
    setAppliedDates(empty);
  };

  const popover = (
    <Popover id="popover-dates" style={{ minWidth: "300px" }} className="shadow-lg border-primary">
      <Popover.Header as="h3" className="bg-primary text-white" style={{ fontSize: "0.75rem" }}>FILTRA PER PERIODO</Popover.Header>
      <Popover.Body>
        <Form>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: "0.65rem", fontWeight: "bold" }}>DA:</Form.Label>
            <Form.Control type="date" size="sm" value={tempDates.start} onChange={(e) => setTempDates({ ...tempDates, start: e.target.value })} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "0.65rem", fontWeight: "bold" }}>A:</Form.Label>
            <Form.Control type="date" size="sm" value={tempDates.end} onChange={(e) => setTempDates({ ...tempDates, end: e.target.value })} />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button variant="primary" size="sm" className="w-100" onClick={handleApplyDates}>Applica</Button>
            <Button variant="outline-secondary" size="sm" onClick={handleResetDates}>Reset</Button>
          </div>
        </Form>
      </Popover.Body>
    </Popover>
  );

  const renderDateBox = (label, dateString) => {
    const isMissing = !dateString || dateString === "null";
    return (
      <Col md={3} className="mb-3 border-end">
        <div className="info-label">{label}</div>
        <div className={`info-value ${isMissing ? "text-muted opacity-50" : ""}`} style={{ fontSize: "0.72rem" }}>
          {isMissing ? "---" : new Date(dateString).toLocaleString("it-IT")}
        </div>
      </Col>
    );
  };

  return (
    <div className="ombra card tb" style={{ borderRadius: "12px", border: "1px solid #dee2e6", height: "750px", display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff" }}>
      
      {/* HEADER CONTROLLI */}
      <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: "4px", height: "18px", backgroundColor: "#0d6efd", borderRadius: "2px" }}></div>
          <span style={{ fontWeight: "800", fontSize: "0.8rem", color: "#2c3e50", letterSpacing: "0.5px" }}>WORKFLOW LOGISTICO</span>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <OverlayTrigger trigger="click" placement="bottom" overlay={popover} rootClose>
            <Button variant="light" size="sm" className="border d-flex align-items-center gap-1" style={{ borderRadius: "8px", fontSize: "0.75rem" }}>
              <Calendar size={14} /> Calendario
            </Button>
          </OverlayTrigger>

          <Form.Select size="sm" style={{ fontSize: "0.75rem", borderRadius: "8px", width: "160px" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="TUTTI">Tutti gli stati</option>
            {[ "ORDINE CONFERMATO", "IN ARRIVO", "IN MAGAZZINO", "PRESO IN CARICO", "BEM INVIATA", "BEM COMPLETATA", "INVIO ODP/ODPV", "COMPLETAMENTO ODP/ODPV", "GESTITO", "CHIUSO CON BOLLA", "COMPLETATO"].map((s) => (
               <option key={s} value={s}>{s}</option>
            ))}
          </Form.Select>

          <div className="position-relative">
            <Search size={14} className="position-absolute" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", color: "#adb5bd" }} />
            <Form.Control size="sm" type="text" placeholder="Cerca ordine, cliente, fornitore..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ fontSize: "0.75rem", borderRadius: "8px", width: "250px", paddingLeft: "30px" }} />
          </div>
        </div>
      </div>

      <style>{`
        .scroll-area { overflow-y: auto; flex-grow: 1; }
        .table-custom thead th { position: sticky; top: 0; z-index: 30; background: #f8f9fa; font-size: 0.65rem; padding: 10px 12px; color: #495057; border-bottom: 2px solid #dee2e6; }
        
        /* Riga Testata Colorata */
        .header-row { background-color: #e9ecef !important; border-left: 4px solid #0d6efd; }
        .header-row td { padding: 10px 12px !important; vertical-align: middle; }
        .header-label { font-size: 0.6rem; font-weight: 800; color: #6c757d; text-transform: uppercase; margin-right: 5px; }
        .header-main-info { font-size: 0.75rem; font-weight: 700; color: #212529; }

        .item-row td { font-size: 0.72rem; padding: 10px 12px; border-bottom: 1px solid #f1f1f1; background: white; vertical-align: middle; }
        .text-art-code { color: #0d6efd; font-weight: 700; font-family: monospace; }
        .info-label { font-size: 0.6rem; color: #999; font-weight: 800; margin-bottom: 2px; }
        .info-value { font-size: 0.75rem; font-weight: 600; color: #333; }
      `}</style>

      <div className="scroll-area">
        <Table hover className="table-custom mb-0">
          <thead>
            <tr>
              <th style={{ width: "20%" }}>DOC / ARTICOLO</th>
              <th style={{ width: "35%" }}>DESCRIZIONE</th>
              <th style={{ width: "15%" }}>DATA ORDINE</th>
              <th style={{ width: "20%" }}>STATO ATTUALE</th>
              <th style={{ width: "10%" }} className="text-center">AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((ordine, index) => {
              const showHeader = index === 0 || ordine["Num Doc"] !== filteredOrders[index - 1]["Num Doc"];

              return (
                <React.Fragment key={index}>
                  {showHeader && (
                    <tr className="header-row">
                      <td colSpan={5} className="bg-dark">
                        <div className="d-flex gap-4">
                          <div><span className="header-label text-light">DOC:</span><span className="header-main-info text-light">{ordine["Num Doc"]}</span></div>
                          <div><span className="header-label text-light">FORNITORE:</span><span className="header-main-info text-light">{ordine["Ragione sociale"]}</span></div>
                          <div><span className="header-label text-light">CLIENTE:</span><span className="header-main-info text-light">{ordine["Rag Soc Cli"]}</span></div>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr className="item-row">
                    <td>
                      <div className="d-flex flex-column">
                        <small className="text-muted" style={{fontSize: '0.6rem'}}>Doc: {ordine["Num Doc"]}</small>
                        <span className="text-art-code">{ordine["Articolo"]}</span>
                      </div>
                    </td>
                    <td className="text-dark">{ordine["Descrizione"]}</td>
                    <td>{new Date(ordine["Data Ordine"]).toLocaleDateString("it-IT")}</td>
                    <td>
                      <Button
                        className={getStatusButtonClass(ordine.STATO)}
                        size="sm"
                        style={{ fontSize: "0.62rem", width: "100%", fontWeight: "600", letterSpacing: "0.3px", border: "none", borderRadius: "5px" }}
                      >
                        {ordine.STATO}
                      </Button>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => { setSelectedOrder(ordine); setShowModal(true); }}
                        style={{ fontSize: "0.65rem", padding: "2px 8px", fontWeight: "700" }}
                      >
                        DETTAGLI
                      </Button>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* MODALE DETTAGLI */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title style={{ fontSize: "0.85rem", fontWeight: "700" }}>
            DETTAGLIO DOC #{selectedOrder?.["Num Doc"]} - ARTICOLO: {selectedOrder?.Articolo}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedOrder && (
            <>
              <Row className="mb-4">
                <Col md={8}>
                  <div className="info-label">Articolo & Descrizione</div>
                  <div className="info-value text-primary" style={{ fontSize: "1.1rem" }}>
                    {selectedOrder.Articolo} <span className="text-dark fw-normal">— {selectedOrder.Descrizione}</span>
                  </div>
                </Col>
                <Col md={4} className="text-end">
                  <div className="info-label">Stato Corrente</div>
                  <Badge bg="dark" className="p-2" style={{fontSize: '0.8rem'}}>{selectedOrder.STATO}</Badge>
                </Col>
              </Row>
              
              <Row className="g-2 border p-3 rounded bg-light mb-4 shadow-sm text-center">
                {renderDateBox("Entrata Magazzino", selectedOrder["Data Entrata Magazzino"])}
                {renderDateBox("Presa in carico", selectedOrder["Data Presa in carico *"])}
                {renderDateBox("Invio BEM", selectedOrder["Data Invio Bem"])}
                {renderDateBox("Completamento BEM", selectedOrder["Data Completamento BEM"])}
                {renderDateBox("Invio ODP/ODPV", selectedOrder["Data Invio ODP\/ODPV"])}
                {renderDateBox("Chiusura ODP/ODPV", selectedOrder["Data Completamento ODP\/ODPV"])}
                {renderDateBox("Data Bolla", selectedOrder["data_bolla"])}
                {renderDateBox("Data Fattura", selectedOrder["data_fatt"])}
              </Row>

              <div className="d-flex justify-content-between p-3 border rounded bg-white shadow-sm">
                <div>
                  <span className="info-label">Q.TÀ ORDINATA:</span> <span className="info-value">{parseInt(selectedOrder.Quantita)}</span>
                  <span className="info-label ms-4">Q.TÀ EVASA:</span> <span className="info-value text-success">{parseInt(selectedOrder["Qta EVA"])}</span>
                </div>
                <div>
                  <span className="info-label">RIMESSO:</span> <span className="info-value text-primary">{selectedOrder["Qta Rimesso"] || 0}</span>
                  <span className="info-label ms-4 text-danger">DA EVADERE (DEV):</span> <span className="info-value text-danger">{parseInt(selectedOrder["Qta DEV"])}</span>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SalesChart;